import { useId, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent, type ReactNode } from "react";
import "./floating-toolbar.css";

type Point = { x: number; y: number };
type Preference = { position?: Point | undefined; collapsed?: boolean };
const prefix = "fengyue.floating-toolbar.v1.";

function readPreference(key: string): Preference {
  try {
    const value = JSON.parse(localStorage.getItem(prefix + key) ?? "{}");
    return {
      ...(Number.isFinite(value?.position?.x) && Number.isFinite(value?.position?.y) ? { position: value.position } : {}),
      ...(typeof value?.collapsed === "boolean" ? { collapsed: value.collapsed } : {}),
    };
  } catch { return {}; }
}

export interface FloatingToolbarProps {
  children: ReactNode;
  storageKey: string;
  label: string;
  enabled?: boolean;
  width?: number;
  collapsed?: boolean;
  onCollapsedChange?: (value: boolean) => void;
  collapseLabel?: string;
  expandLabel?: string;
  reserveSpace?: boolean;
  showCollapsedControl?: boolean;
}

/** A manual popover escapes editor/canvas clipping without moving out of its focus scope. */
export function FloatingToolbar({ children, storageKey, label, enabled = true, width = 840,
  collapsed: controlledCollapsed, onCollapsedChange, collapseLabel = `收起${label}`,
  expandLabel = `展开${label}`, reserveSpace = false, showCollapsedControl = true }: FloatingToolbarProps) {
  const [preference, setPreference] = useState(() => readPreference(storageKey));
  const collapsed = controlledCollapsed ?? preference.collapsed ?? false;
  const hidden = collapsed && !showCollapsedControl;
  const position = useRef(preference.position);
  const anchor = useRef<HTMLDivElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const gesture = useRef<{ id: number; start: Point; origin: Point; saved: Point | undefined; handle: HTMLButtonElement } | undefined>(undefined);
  const placeRef = useRef<() => void>(() => {});
  const [dragging, setDragging] = useState(false);
  const contentId = useId();

  const save = (patch: Preference) => {
    setPreference((previous) => {
      const next = { ...previous, ...patch };
      try { localStorage.setItem(prefix + storageKey, JSON.stringify(next)); } catch { /* Optional UI preferences. */ }
      return next;
    });
  };

  useLayoutEffect(() => {
    if (!enabled || !panel.current || !anchor.current) return;
    const element = panel.current;
    // Top-layer placement also works inside native handwriting dialogs and fullscreen canvases.
    const syncVisibility = () => {
      if (typeof element.showPopover !== "function") return;
      const dialog = element.closest("dialog");
      if (hidden || (dialog && !dialog.open) || element.closest('[inert], [aria-hidden="true"]')) element.hidePopover?.();
      else if (!element.matches(":popover-open")) element.showPopover?.();
    };
    // App dialogs mark their background inert. Keep its palette behind the dialog too.
    const visibilityObserver = typeof MutationObserver === "undefined" ? undefined : new MutationObserver(syncVisibility);
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
      visibilityObserver?.observe(parent, { attributes: true, attributeFilter: ["inert", "aria-hidden", "open"] });
    }
    syncVisibility();
    const place = () => {
      const viewport = window.visualViewport;
      const left = viewport?.offsetLeft ?? 0;
      const top = viewport?.offsetTop ?? 0;
      const viewportWidth = viewport?.width ?? window.innerWidth;
      const viewportHeight = viewport?.height ?? window.innerHeight;
      element.style.maxWidth = `${Math.max(100, viewportWidth - 20)}px`;
      element.style.setProperty("--floating-toolbar-max-height", `${Math.max(72, viewportHeight - 24)}px`);
      const bounds = element.getBoundingClientRect();
      const origin = anchor.current!.getBoundingClientRect();
      const next = position.current ?? { x: origin.left + (origin.width - bounds.width) / 2, y: origin.top };
      element.style.left = `${Math.max(left + 10, Math.min(next.x, left + viewportWidth - bounds.width - 10))}px`;
      const headerInset = !position.current && !element.closest("dialog") ? Number.parseFloat(getComputedStyle(anchor.current!).getPropertyValue("--knowledge-sticky-offset")) || 0 : 0;
      const minTop = top + Math.min(headerInset + 10, Math.max(10, viewportHeight - bounds.height - 10));
      element.style.top = `${Math.max(minTop, Math.min(next.y, top + viewportHeight - bounds.height - 10))}px`;
    };
    placeRef.current = place;
    place();
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(place);
    observer?.observe(element);
    observer?.observe(anchor.current);
    if (anchor.current.parentElement?.parentElement) observer?.observe(anchor.current.parentElement.parentElement);
    const frame = requestAnimationFrame(place);
    window.addEventListener("scroll", place, true);
    window.addEventListener("resize", place);
    window.visualViewport?.addEventListener("resize", place);
    window.visualViewport?.addEventListener("scroll", place);
    return () => {
      observer?.disconnect();
      visibilityObserver?.disconnect();
      cancelAnimationFrame(frame);
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("resize", place);
      window.visualViewport?.removeEventListener("scroll", place);
      element.hidePopover?.();
    };
  }, [enabled, hidden]);

  const finish = (cancel: boolean) => {
    const drag = gesture.current;
    if (!drag) return;
    gesture.current = undefined;
    if (cancel) position.current = drag.saved;
    else save({ position: position.current });
    if (drag.handle.hasPointerCapture?.(drag.id)) drag.handle.releasePointerCapture(drag.id);
    setDragging(false);
    placeRef.current();
  };

  useLayoutEffect(() => {
    if (!dragging) return;
    const cancel = (event: KeyboardEvent) => {
      if (event.key === "Escape") { event.preventDefault(); event.stopPropagation(); finish(true); }
    };
    const cancelOnBlur = () => finish(true);
    window.addEventListener("keydown", cancel, true);
    window.addEventListener("blur", cancelOnBlur);
    return () => {
      window.removeEventListener("keydown", cancel, true);
      window.removeEventListener("blur", cancelOnBlur);
    };
  }, [dragging]);

  const start = (event: PointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0 || gesture.current || !panel.current) return;
    event.preventDefault();
    event.stopPropagation();
    const rect = panel.current.getBoundingClientRect();
    gesture.current = { id: event.pointerId, start: { x: event.clientX, y: event.clientY },
      origin: { x: rect.left, y: rect.top }, saved: position.current, handle: event.currentTarget };
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };

  if (!enabled) return <>{children}</>;
  return <div ref={anchor} className={`floating-toolbar-anchor${reserveSpace && !hidden ? " floating-toolbar-anchor--reserved" : ""}`}>
    <div ref={panel} {...{ popover: "manual" }} hidden={hidden} className="floating-toolbar" data-collapsed={collapsed} data-dragging={dragging}
      aria-label={label} style={{ "--floating-toolbar-width": `${width}px` } as CSSProperties}
      onKeyDownCapture={(event) => { if (event.key === "Escape" && gesture.current) { event.preventDefault(); event.stopPropagation(); finish(true); } }}>
      <button type="button" className="floating-toolbar__handle" aria-label={`移动${label}`}
        title="拖动调整位置；聚焦后用方向键移动，Shift 加速，Esc 取消拖动"
        onPointerDown={start} onMouseDown={(event) => event.preventDefault()}
        onPointerMove={(event) => {
          const drag = gesture.current;
          if (!drag || drag.id !== event.pointerId) return;
          event.preventDefault(); event.stopPropagation();
          position.current = { x: drag.origin.x + event.clientX - drag.start.x, y: drag.origin.y + event.clientY - drag.start.y };
          placeRef.current();
        }}
        onPointerUp={(event) => { if (event.pointerId === gesture.current?.id) { event.stopPropagation(); finish(false); } }}
        onPointerCancel={(event) => { if (event.pointerId === gesture.current?.id) finish(true); }}
        onLostPointerCapture={() => finish(true)}
        onKeyDown={(event) => {
          const delta = { ArrowLeft: [-1, 0], ArrowRight: [1, 0], ArrowUp: [0, -1], ArrowDown: [0, 1] }[event.key];
          if (!delta || !panel.current) return;
          event.preventDefault(); event.stopPropagation();
          const rect = panel.current.getBoundingClientRect();
          const step = event.shiftKey ? 40 : 10;
          position.current = { x: rect.left + delta[0]! * step, y: rect.top + delta[1]! * step };
          save({ position: position.current }); placeRef.current();
        }}><svg aria-hidden="true" width="12" height="20" viewBox="0 0 12 20" fill="currentColor"><circle cx="4" cy="5" r="1"/><circle cx="8" cy="5" r="1"/><circle cx="4" cy="10" r="1"/><circle cx="8" cy="10" r="1"/><circle cx="4" cy="15" r="1"/><circle cx="8" cy="15" r="1"/></svg></button>
      <div id={contentId} hidden={collapsed} className="floating-toolbar__content">{children}</div>
      {collapsed ? <span className="floating-toolbar__label">{label}</span> : null}
      <div className="floating-toolbar__controls">
        <button type="button" aria-label={`重置${label}位置`} title="重置工具栏位置" onMouseDown={(event) => event.preventDefault()}
          onClick={() => { position.current = undefined; save({ position: undefined }); placeRef.current(); }}>⟲</button>
        <button type="button" aria-label={collapsed ? expandLabel : collapseLabel} title={collapsed ? expandLabel : collapseLabel}
          aria-expanded={!collapsed} aria-controls={contentId} onMouseDown={(event) => event.preventDefault()}
          onClick={() => { save({ collapsed: !collapsed }); onCollapsedChange?.(!collapsed); }}>
          {collapsed ? "＋" : "−"}
        </button>
      </div>
    </div>
  </div>;
}
